import * as React from 'react'
import * as RD from '@devexperts/remote-data-ts'
import * as domain from '@packages/domain'

export interface UsePreviewAdsGateArgs {
  previewData: RD.RemoteData<domain.Error.Type, domain.Api.IAGenPreviewResponse.Type>
  minDisplaySeconds: number
  hasAds: boolean
}

export interface PreviewAdsGate {
  /** Show the ad view (carousel + Next button) instead of the normal preview fold. */
  visible: boolean
  /** The preview render has resolved successfully. */
  previewReady: boolean
  /** Next button may be activated (previewReady AND min-time elapsed). */
  canAdvance: boolean
  /** User clicked Next — reveal the result view. */
  advance: () => void
}

export const usePreviewAdsGate = ({ previewData, minDisplaySeconds, hasAds }: UsePreviewAdsGateArgs): PreviewAdsGate => {
  const [armed, setArmed] = React.useState(false)
  const [advanced, setAdvanced] = React.useState(false)
  const [minTimeElapsed, setMinTimeElapsed] = React.useState(false)

  const isPending = RD.isPending(previewData)
  const isSuccess = RD.isSuccess(previewData)
  const isInitial = RD.isInitial(previewData)

  const prevPending = React.useRef(isPending)
  const prevInitial = React.useRef(isInitial)

  // Arm on the initial -> pending edge: a fresh generation in this session.
  React.useEffect(() => {
    if (isPending && !prevPending.current) {
      setArmed(true)
      setAdvanced(false)
      setMinTimeElapsed(false)
    }
    prevPending.current = isPending
  }, [isPending])

  // Disarm on the edge back to initial (reset).
  React.useEffect(() => {
    if (isInitial && !prevInitial.current) {
      setArmed(false)
      setAdvanced(false)
      setMinTimeElapsed(false)
    }
    prevInitial.current = isInitial
  }, [isInitial])

  // Min-time clock: starts when armed.
  React.useEffect(() => {
    if (!armed) return
    if (minDisplaySeconds <= 0) {
      setMinTimeElapsed(true)
      return
    }
    const id = setTimeout(() => setMinTimeElapsed(true), minDisplaySeconds * 1000)
    return () => clearTimeout(id)
  }, [armed, minDisplaySeconds])

  const visible = hasAds && armed && (isPending || (isSuccess && !advanced))
  const canAdvance = isSuccess && minTimeElapsed
  const advance = React.useCallback(() => setAdvanced(true), [])

  return { visible, previewReady: isSuccess, canAdvance, advance }
}
